import useApiRequest from "@/lib/hooks/use-request";
import { formatDateTime } from "@/lib/hooks/utils";
import { PersonDetailsResponseDto } from "@/lib/interfaces/persons";
import PageNotFound from "@/page-components/error/page-not-found";
import PipelineLinked from "@/page-components/pipelines/pipeline-linked";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/ui-components/ui/accordion";
import { Badge } from "@/ui-components/ui/badge";
import { useBreadcrumb } from "@/ui-components/ui/breadcrumb/breadcrumb-context";
import { Button } from "@/ui-components/ui/button";
import DataFetchParent from "@/ui-components/ui/data-fetch";
import { Table, TableBody, TableCell, TableRow } from "@/ui-components/ui/table";
import {
  AccordionContainerParent,
  AccordionContainerSmall,
} from "@/ui-components/utils/menu-items/accordion-container-manager";
import { PageTitle, PageTitleContainer } from "@/ui-components/utils/menu-items/page-title";
import { Building2, Edit2, Mail, Phone, User, X } from "lucide-react";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import OrganisationLinked from "../../organisations-linked";
import PersonEditor from "../person-editor";
import Tippy from "@tippyjs/react";


const ContactPersonsDetails = () => {
  const { id } = useParams();
  return <>{id ? <ContactPersonsFetcher id={id} /> : <PageNotFound />}</>;
};

const ContactPersonsFetcher = ({ id }: { id: string }) => {
  const [item, setItem] = useState<PersonDetailsResponseDto>();
  const [isLoading, setIsLoading] = useState(false);
  const { apiRequest } = useApiRequest();
  const [reload, setReload] = useState(false);

  const getItem = useCallback(async () => {
    setIsLoading(true);
    const response = await apiRequest<PersonDetailsResponseDto>(`contacts/persons/${id}`, "GET");
    setIsLoading(false);
    if (response.data) {
      setItem(response.data);
    }
  }, [apiRequest, id]);

  useEffect(() => {
    getItem();
  }, [getItem, reload]);

  return (
    <DataFetchParent
      isLoading={isLoading}
      data={item}
      renderElement={(loadedItem: PersonDetailsResponseDto) => (
        <PersonDetails setReload={setReload} item={loadedItem} />
      )}
    />
  );
};

const PersonDetails = ({
  item,
  setReload,
}: {
  item: PersonDetailsResponseDto;
  setReload: Dispatch<SetStateAction<boolean>>;
}) => {
  const { setTitle } = useBreadcrumb();
  const [isEditing, setIsEditing] = useState(false);
  const { apiRequest } = useApiRequest();

  useEffect(() => {
    setTitle(item.fullName);
  }, [item, setTitle]);

  const assignOrga = async (id: string) => {
    await apiRequest(`contacts/persons/${item._id}`, "PATCH", {
      body: {
        belongsToOrganizationId: id,
      },
    });
    setReload((prev) => !prev);
  };

  const removeLinkedOrga = async () => {
    await apiRequest(`contacts/persons/${item._id}/unset-organization`, "PATCH");
    setReload((prev) => !prev);
  };

  const assignPipeline = async (pipelineId: string, title: string) => {
    await apiRequest(`contacts/persons/${item._id}/pipelines`, "POST", {
      body: { pipelineId },
    });
    setReload((prev) => !prev);
  };

  const removeLinkedPipeline = async (pipelineId: string) => {
    await apiRequest(`contacts/persons/${item._id}/pipelines/${pipelineId}`, "DELETE");
    setReload((prev) => !prev);
  };

  return (
    <div className="h-full flex flex-col">
      <PageTitleContainer>
        <div className="flex flex-row justify-between items-center">
          <div className="flex items-center gap-2">
            <PageTitle>Details {item.fullName}</PageTitle>
            {item.organizationPreview && (
              <Link to={`/contacts/organisations/${item.organizationPreview._id}`}>
                <Badge variant="secondary" className="flex items-center gap-1">
                  <Building2 className="h-4 w-4" />
                  {item.organizationPreview.name}
                </Badge>
              </Link>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setIsEditing(!isEditing)} className="gap-2" variant="secondary">
              <span>{isEditing ? "Abbrechen" : "Bearbeiten"}</span>
              {isEditing ? <X size={16} /> : <Edit2 size={16} />}
            </Button>
          </div>
        </div>
      </PageTitleContainer>
      <div className="grow">
        {isEditing ? (
          <div className="p-4 w-1/2 items-center m-auto">
            <PersonEditor
              item={item}
              onSubmitDone={() => {
                setIsEditing(false);
                setReload((prev) => !prev);
              }}
            />
          </div>
        ) : (
          <AccordionContainerParent>
            <AccordionContainerSmall>
              <Accordion type="multiple" className="space-y-4">
                <AccordionItem value="organization" className="border rounded-lg">
                  <AccordionTrigger className="px-4">Unternehmen</AccordionTrigger>
                  <AccordionContent className="px-4 pb-4 m-0">
                    <OrganisationLinked
                      item={item.organizationPreview}
                      onAssign={assignOrga}
                      onRemove={removeLinkedOrga}
                    />
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="deals" className="border rounded-lg">
                  <AccordionTrigger className="px-4">Deals</AccordionTrigger>
                  <AccordionContent className="px-4 pb-4 m-0">
                    {item.dealsPreview && item.dealsPreview.length > 0 ? (
                      <div className="flex flex-col">
                        {item.dealsPreview.map((deal) => (
                          <div key={deal._id} className="flex justify-between items-center py-3 border-b last:border-b-0">
                            <div className="flex items-center">
                              <Link to={`/deals/${deal._id}`} className="text-blue-600 hover:text-blue-800 hover:underline">
                                {deal.title}
                              </Link>
                            </div>
                            <div className="flex items-center">
                              <div className="flex flex-col items-end gap-1">
                              {deal.pipelineTitle && (
                                  <Tippy content={deal.pipelineTitle}>
                                    <div className="text-xs text-gray-500 max-w-[200px] truncate">
                                      {deal.pipelineTitle.length > 20 
                                        ? `${deal.pipelineTitle.substring(0, 20)}...` 
                                        : deal.pipelineTitle}
                                    </div>
                                  </Tippy>
                                )}
                                <div className="flex gap-1">
                                  {deal.isWon === true && (
                                    <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">
                                      Gewonnen
                                    </span>
                                  )}
                                  {deal.isWon === false && (
                                    <span className="px-1.5 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                                      Verloren
                                    </span>
                                  )}
                                  {deal.isArchived ? (
                                    <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-800 rounded-full">
                                      Archiviert
                                    </span>
                                  ) : (
                                    <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                                      Offen
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-gray-500 italic">Keine Deals verlinkt</div>
                    )}
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="pipelines" className="border rounded-lg">
                  <AccordionTrigger className="px-4">Pipelines</AccordionTrigger>
                  <AccordionContent className="px-4 pb-4 m-0">
                    <PipelineLinked
                      multiple
                      item={item.pipelinesPreview}
                      onAssign={assignPipeline}
                      onRemove={removeLinkedPipeline}
                    />
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </AccordionContainerSmall>
            <div className="grow">
              <Table className="w-full">
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        Vorname
                      </div>
                    </TableCell>
                    <TableCell>{item.firstName}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Nachname</TableCell>
                    <TableCell>{item.lastName || "-"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        E-Mail
                      </div>
                    </TableCell>
                    <TableCell>
                      {item.mail ? (
                        <Link
                          className="flex items-center gap-2 cursor-pointer"
                          to={`mailto:${item.mail}`}
                          target="_blank"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button variant="link" className="flex items-center gap-2 h-fit p-0">
                            <Mail className="h-4 w-4" />
                            <span>{item.mail}</span>
                          </Button>
                        </Link>
                      ) : (
                        <span>-</span>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4" />
                        Telefon
                      </div>
                    </TableCell>
                    <TableCell>
                      {item.phone ? (
                        <Link
                          className="flex items-center gap-2 cursor-pointer"
                          to={`tel:${item.phone}`}
                          target="_blank"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button variant="link" className="flex items-center gap-2 h-fit p-0">
                            <Phone className="h-4 w-4" />
                            <span>{item.phone}</span>
                          </Button>
                        </Link>
                      ) : (
                        <span>-</span>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Straße</TableCell>
                    <TableCell>{item.street || "-"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Postleitzahl</TableCell>
                    <TableCell>{item.postalCode || "-"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Stadt</TableCell>
                    <TableCell>{item.city || "-"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Land</TableCell>
                    <TableCell>{item.country || "-"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Erstellt am</TableCell>
                    <TableCell>{formatDateTime(item.createdAt)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Zuletzt geändert am</TableCell>
                    <TableCell>{formatDateTime(item.updatedAt)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </AccordionContainerParent>
        )}
      </div>
    </div>
  );
};

export default ContactPersonsDetails;
